import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import {
  buildClientName,
  buildOrganizationAddress,
} from 'receivable-invoices-js/react/utils/mandates';
import { apiBaseURL } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { PAYMENT_FREQUENCIES } from 'receivable-invoices-js/react/constants/mandates';
import type { Mandate, MandateResponseAPI } from '../../models/mandate';

interface Params {
  id: string;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${apiBaseURL}/v1/direct_debit_collection_mandates/shared`;

export function fetchMandateKey(id: string): QueryKey {
  return ['mandate', id] as QueryKey;
}

export async function fetchMandate(params: Params, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const response = await qontoClient<MandateResponseAPI>(`${URL}/${params.id}`);

    const { data } = response;
    const { attributes } = data;
    const {
      client_snapshot: clientSnapshot,
      organization_snapshot: organizationSnapshot,
      payment_info: paymentInfo,
    } = attributes;

    const clientAddress = clientSnapshot.address;
    const clientZipCode = clientSnapshot.zip_code;
    const clientCity = clientSnapshot.city;
    const clientCountryCode = clientSnapshot.country_code;
    const clientEmail = clientSnapshot.email;

    const addressWasPrefilled = Boolean(
      clientAddress || clientZipCode || clientCity || clientCountryCode
    );

    const isSddRecurring = paymentInfo.schedule_type !== PAYMENT_FREQUENCIES.ONE_OFF;

    const mandate: Mandate = {
      mandateId: data.id,
      sddScheduleType: paymentInfo.schedule_type,
      sddScheduleCount: paymentInfo.schedule_total_collection_count,
      sddCustomScheduleUnit: paymentInfo.schedule_custom_unit,
      sddCustomScheduleInterval: paymentInfo.interval,
      sddAmount: paymentInfo.first_payment.amount,
      sddCollectionDate: paymentInfo.first_payment.collection_date,
      sddReference: paymentInfo.first_payment.reference,
      sddIsOverdue: paymentInfo.first_payment.is_overdue,
      clientName: buildClientName(clientSnapshot),
      clientAddress,
      clientZipCode,
      clientCity,
      clientCountryCode,
      clientProvinceCode: '',
      clientIban: '',
      clientLocale: clientSnapshot.locale,
      clientEmail,
      organizationName: organizationSnapshot.name,
      organizationAddress: buildOrganizationAddress(organizationSnapshot),
      organizationLogoUrl: organizationSnapshot.logo_url || '',
      organizationCreditorIdentifier: attributes.creditor_identifier,
      addressWasPrefilled,
      isSddRecurring,
      mandateReferenceCode: attributes.unique_mandate_reference,
    };

    return mandate;
  } catch (error: unknown) {
    if (onError) {
      onError(error);
      return null;
    }
    throw error;
  }
}

export type FetchMandateResponse = Awaited<ReturnType<typeof fetchMandate>>;

export function useFetchMandate(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchMandateResponse>>
) {
  return useQuery({
    queryKey: fetchMandateKey(params.id),
    queryFn: () => fetchMandate(params, { ...customOptions }),
    ...queryOptions,
  });
}
